/**
 * The week's standing pins, loaded once for the whole board: the standing
 * crew panel lists them, cards read "pinned until …" from them, and every
 * write that touches pins (extend, end, remove, drop on a worker row) calls
 * `reload`. The rows live in the roster slice under the same plant × week
 * key as the assignments.
 */
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { StoreType } from "../../../../../utils/types";
import { localDay } from "../../../utils/time";
import { fetchRosterPins, selectRosterWeek } from "../../store/roster-slice";
import { RosterWeekKey } from "../../store/roster-types";

export function useStandingPins(locationId: number, weekStart: Date) {
	const dispatch = useDispatch<ThunkDispatch<StoreType, unknown, AnyAction>>();
	const key = useMemo<RosterWeekKey>(() => ({ locationId, weekStart: localDay(weekStart) }), [locationId, weekStart]);
	const week = useSelector((state: StoreType) => selectRosterWeek(state, key));

	const reload = useCallback(async () => {
		if (!locationId) {
			return;
		}
		await dispatch(fetchRosterPins(key));
	}, [dispatch, key, locationId]);

	useEffect(() => {
		void reload();
	}, [reload]);

	const { pins, pinsStatus, pinsError } = week;
	return useMemo(
		() => ({ pins, loading: pinsStatus === "pending", error: pinsError, reload }),
		[pins, pinsStatus, pinsError, reload]
	);
}

export type StandingPins = ReturnType<typeof useStandingPins>;
